import React, { useMemo, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Download, RefreshCw, Search } from 'lucide-react';

const PAGE_SIZE = 200;

const GET_CHANGE_AUDIT = gql`
  query GetChangeAudit(
    $system: String
    $startDate: Date
    $endDate: Date
    $search: String
    $orderBy: String
    $orderDir: String
    $page: Int
    $pageSize: Int
  ) {
    getChangeAudit(
      system: $system
      startDate: $startDate
      endDate: $endDate
      search: $search
      orderBy: $orderBy
      orderDir: $orderDir
      page: $page
      pageSize: $pageSize
    ) {
      count
      items {
        system
        candidateId
        name
        auditAt
        auditBy
        auditByName
        editors
        changeTypes
        windowStart
        windowEnd
        changes {
          field
          before
          after
        }
      }
    }
  }
`;

interface ChangeField {
  field: string;
  before: string | null;
  after: string | null;
}

interface ChangeAuditEntry {
  system: string;
  candidateId: number;
  name: string | null;
  auditAt: string | null;
  auditBy: string | null;
  auditByName: string | null;
  editors: string[];
  changeTypes: string[];
  windowStart: string | null;
  windowEnd: string | null;
  changes: ChangeField[];
}

interface ChangeAuditResult {
  count: number;
  items: ChangeAuditEntry[];
}

export const ChangeAuditPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [orderBy, setOrderBy] = useState<'date' | 'editor'>('date');
  const [orderDir, setOrderDir] = useState<'desc' | 'asc'>('desc');
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { data, loading, error, refetch } = useQuery<{ getChangeAudit: ChangeAuditResult }>(GET_CHANGE_AUDIT, {
    variables: {
      system: 'LAI_JAIL',
      startDate: startDate || null,
      endDate: endDate || null,
      search: submittedSearch || null,
      orderBy,
      orderDir,
      page,
      pageSize: PAGE_SIZE,
    },
    fetchPolicy: 'no-cache',
  });

  const items = data?.getChangeAudit?.items || [];
  const totalCount = data?.getChangeAudit?.count || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const handleSearch = () => {
    setPage(1);
    setSubmittedSearch(searchTerm.trim());
  };

  const handleClear = () => {
    setSearchTerm('');
    setSubmittedSearch('');
    setStartDate('');
    setEndDate('');
    setOrderBy('date');
    setOrderDir('desc');
    setPage(1);
  };

  const handlePageChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && page > 1) {
      setPage(page - 1);
      return;
    }

    if (direction === 'next' && page < totalPages) {
      setPage(page + 1);
    }
  };

  const formattedItems = useMemo(() => {
    return items.map((entry) => ({
      ...entry,
      auditAtDisplay: entry.auditAt ? new Date(entry.auditAt).toLocaleString() : 'N/A',
      windowDisplay: entry.windowStart && entry.windowEnd
        ? `${new Date(entry.windowStart).toLocaleString()} - ${new Date(entry.windowEnd).toLocaleString()}`
        : 'N/A',
    }));
  }, [items]);

  const changeColumns = useMemo(() => {
    const columns = new Set<string>();
    items.forEach((entry) => {
      entry.changes.forEach((change) => {
        if (change.field) {
          columns.add(change.field);
        }
      });
    });
    return Array.from(columns).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const handleDownloadCsv = () => {
    const escapeCsv = (value: string) => {
      if (value.includes('"') || value.includes(',') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    const headers = [
      'System',
      'Candidate ID',
      'Name',
      'Audit At',
      'Audit By',
      'Audit By Name',
      'Editors',
      'Change Types',
      'Window Start',
      'Window End',
      ...changeColumns,
    ];

    const rows = items.map((entry) => {
      const changeMap = new Map<string, string>();
      entry.changes.forEach((change) => {
        const before = change.before ?? '';
        const after = change.after ?? '';
        if (before === '' && after === '') {
          changeMap.set(change.field, '');
          return;
        }
        if (before === '') {
          changeMap.set(change.field, after);
          return;
        }
        if (after === '') {
          changeMap.set(change.field, before);
          return;
        }
        changeMap.set(change.field, `${before} -> ${after}`);
      });

      return [
        entry.system ?? '',
        entry.candidateId?.toString() ?? '',
        entry.name ?? '',
        entry.auditAt ?? '',
        entry.auditBy ?? '',
        entry.auditByName ?? '',
        entry.editors?.join('; ') ?? '',
        entry.changeTypes?.join('; ') ?? '',
        entry.windowStart ?? '',
        entry.windowEnd ?? '',
        ...changeColumns.map((field) => changeMap.get(field) ?? ''),
      ];
    });

    const csv = [headers, ...rows]
      .map((row) => row.map((value) => escapeCsv(String(value))).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    link.href = url;
    link.download = `change-audit-${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Change Audit</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="change-audit-search" className="text-sm font-medium text-gray-700 mb-1 block">Search</label>
              <Input
                id="change-audit-search"
                placeholder="Search by name, editor, field, value..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <div>
              <label htmlFor="change-audit-start-date" className="text-sm font-medium text-gray-700 mb-1 block">Start Date</label>
              <Input
                id="change-audit-start-date"
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
              />
            </div>
            <div>
              <label htmlFor="change-audit-end-date" className="text-sm font-medium text-gray-700 mb-1 block">End Date</label>
              <Input
                id="change-audit-end-date"
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Sort By</label>
              <Select value={orderBy} onValueChange={(value) => setOrderBy(value as 'date' | 'editor')}>
                <SelectTrigger>
                  <SelectValue placeholder="Sort By" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Order</label>
              <Select value={orderDir} onValueChange={(value) => setOrderDir(value as 'asc' | 'desc')}>
                <SelectTrigger>
                  <SelectValue placeholder="Order" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">Newest first</SelectItem>
                  <SelectItem value="asc">Oldest first</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button onClick={handleSearch} className="flex items-center gap-2">
              <Search className="h-4 w-4" />
              Search
            </Button>
            <Button variant="outline" onClick={handleClear}>
              Clear
            </Button>
            <Button
              variant="outline"
              onClick={handleDownloadCsv}
              className="flex items-center gap-2"
              disabled={items.length === 0}
            >
              <Download className="h-4 w-4" />
              Download CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => refetch()}
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            <span className="text-sm text-gray-500 ml-auto">
              {loading ? 'Loading...' : `${totalCount} change group(s)`}
            </span>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert>
          <AlertDescription>Failed to load change audit: {error.message}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle>LAI Jail</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Window</TableHead>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Editor</TableHead>
                  <TableHead>Changes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      <div className="flex items-center justify-center">
                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                        Loading change audit...
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && formattedItems.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                      No change audit entries found
                    </TableCell>
                  </TableRow>
                )}
                {!loading && formattedItems.map((entry) => (
                  <TableRow
                    key={`${entry.candidateId}-${entry.windowStart || entry.auditAt}`}
                    className="align-top"
                  >
                    <TableCell className="whitespace-nowrap text-sm">
                      {entry.auditAtDisplay}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-gray-500">
                      {entry.windowDisplay}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{entry.name || 'Unknown'}</div>
                      <div className="text-xs text-gray-500">ID: {entry.candidateId}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="font-medium">{entry.auditBy ?? 'N/A'}</div>
                      {entry.auditByName !== null && entry.auditByName !== '' && (
                        <div className="text-xs text-gray-500">{entry.auditByName}</div>
                      )}
                      {entry.editors && entry.editors.length > 1 && (
                        <div className="text-xs text-gray-500">+{entry.editors.length - 1} more</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {entry.changes.map((change) => (
                          <div key={change.field} className="text-sm">
                            <span className="font-medium text-gray-700">{change.field}</span>
                            <span className="text-gray-500">: </span>
                            {change.before !== null && (
                              <span className="text-gray-500">{change.before} → </span>
                            )}
                            <span className="text-gray-900">{change.after ?? '—'}</span>
                          </div>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-center gap-4 pb-6">
        <Button variant="outline" onClick={() => handlePageChange('prev')} disabled={page <= 1}>
          ← Previous
        </Button>
        <span className="text-sm text-gray-600">
          Page {page} of {totalPages} • Showing {items.length} / {totalCount}
        </span>
        <Button variant="outline" onClick={() => handlePageChange('next')} disabled={page >= totalPages}>
          Next →
        </Button>
      </div>
    </div>
  );
};
